import {
  AfterViewInit, Component, ElementRef, Inject, Input, OnChanges, SimpleChanges, ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { GoogleMapsLoaderService } from '../../core/google-maps-loader.service';

export type Coords = { lat: number; lng: number };

@Component({
  selector: 'app-map-view',
  standalone: true,
  template: `<div #mapEl class="map-view-root"></div>`,
  styles: [`
    :host { display: block; width: 100%; height: 100%; }
    .map-view-root { width: 100%; height: 100%; border-radius: 10px; overflow: hidden; }
  `]
})
export class MapViewComponent implements AfterViewInit, OnChanges {
  @ViewChild('mapEl', { static: true }) mapEl!: ElementRef<HTMLDivElement>;

  @Input() center!: Coords;
  @Input() zoom = 15;

  /** Muestra/oculta el pin */
  @Input() marker = true;

  /** Motor del marcador: 'classic' (preciso) o 'advanced' (HTML). Default: classic */
  @Input() markerEngine: 'classic' | 'advanced' = 'classic';

  /** Semántica del pin (elige icono) */
  @Input() categoria?: string;
  @Input() codigo?: string;       // p.ej. "10-4-1"

  /** Tamaño visual del icono (en px en pantalla) */
  @Input() iconWidth = 45;
  @Input() iconHeight = 45;

  /** Ancla del icono: punto (x,y) del PNG que se clava en la coordenada.
   *  Por defecto: (mitad, base). Ajusta si tu PNG tiene sombra/margen inferior. */
  @Input() iconAnchorX?: number; // default: iconWidth / 2
  @Input() iconAnchorY?: number; // default: iconHeight

  private map?: google.maps.Map;
  private advMarker?: google.maps.marker.AdvancedMarkerElement;
  private classicMarker?: google.maps.Marker;
  private googleRef!: typeof google;

  constructor(
    private loader: GoogleMapsLoaderService,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  async ngAfterViewInit() {
    this.googleRef = await this.loader.load();

    this.map = new this.googleRef.maps.Map(this.mapEl.nativeElement, {
      center: this.center,
      zoom: this.zoom,
      disableDefaultUI: true,
      // mapId: '934c510f10dc83c86061de4d' // si usas MapID, déjalo
    });

    await this.renderMarker(); // crea el pin inicial si corresponde
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!this.map) return;

    if (changes['center'] && this.center) {
      this.map.setCenter(this.center);
      if (this.advMarker) this.advMarker.position = this.center as any;
      if (this.classicMarker) this.classicMarker.setPosition(this.center);
    }

    // Si cambia algo visual, re-crea el marker
    const visualInputs = ['categoria', 'codigo', 'iconWidth', 'iconHeight', 'marker', 'markerEngine', 'iconAnchorX', 'iconAnchorY'];
    if (visualInputs.some(k => k in changes)) {
      await this.renderMarker(true);
    }
  }

  private async renderMarker(forceRebuild = false) {
    if (!this.marker || !this.center) {
      this.removeMarkers();
      return;
    }
    if (forceRebuild) this.removeMarkers();

    const url = this.resolveIconUrl();

    if (this.markerEngine === 'advanced') {
      // AdvancedMarker (visualmente flexible; a veces se nota subpíxel)
      try {
        const { AdvancedMarkerElement } =
          (await this.googleRef.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

        const img = document.createElement('img');
        img.src = url;
        img.width = this.iconWidth;
        img.height = this.iconHeight;
        img.alt = 'pin';
        img.style.userSelect = 'none';
        img.style.pointerEvents = 'none';
        img.style.willChange = 'transform';
        // ancla visual al centro-abajo
        img.style.transform = 'translate3d(-50%, -100%, 0)';

        img.addEventListener('error', () => { img.src = this.asset('default.png'); });

        this.advMarker = new AdvancedMarkerElement({
          map: this.map!,
          position: this.center as any,
          content: img
        });
        return;
      } catch {
        // si falla la librería de advanced markers, caeremos al clásico
      }
    }

    // Marker clásico: MÁS ESTABLE (recomendado)
    const w = this.iconWidth;
    const h = this.iconHeight;
    const anchorX = (this.iconAnchorX ?? (w / 2));
    const anchorY = (this.iconAnchorY ?? h);

    this.classicMarker = new this.googleRef.maps.Marker({
      map: this.map!,
      position: this.center,
      clickable: false,
      optimized: true,
      zIndex: 1000,
      icon: {
        url,
        // tamaño final pintado en pantalla
        size: new this.googleRef.maps.Size(w, h),
        scaledSize: new this.googleRef.maps.Size(w, h),
        // punto exacto del PNG que se clava en la coordenada:
        anchor: new this.googleRef.maps.Point(anchorX, anchorY),
        origin: new this.googleRef.maps.Point(0, 0)
      }
    });
  }

  private removeMarkers() {
    if (this.advMarker) { this.advMarker.map = null; this.advMarker = undefined; }
    if (this.classicMarker) { this.classicMarker.setMap(null as any); this.classicMarker = undefined; }
  }

  /** Construye URL de assets respetando baseHref/subrutas */
  private asset(file: string): string {
    return new URL(`assets/map-pins/${file}`, this.doc.baseURI).toString();
  }

  /** Elige el ícono por CÓDIGO 10-x (prioridad) y luego por CATEGORÍA */
  private resolveIconUrl(): string {
    // 1) Por código (10-x-y). Tomamos el segundo número para familia principal.
    const code = (this.codigo || '').toLowerCase();
    const m = code.match(/^(\d+)-(\d+)/); // ej. 10-4-1
    if (m && m[1] === '10') {
      switch (m[2]) {
        case '0':  return this.asset('estructural.png');
        case '1':  return this.asset('vehicular.png');
        case '2':  return this.asset('pastizal.png');
        case '3':  return this.asset('rescate_personas.png');
        case '4':  return this.asset('rescate_vehicular.png'); // 10-4-x
        case '5':  return this.asset('hazmat.png');
        case '6':  return this.asset('gas.png');
        case '7':  return this.asset('electrico.png');
        case '8':  return this.asset('no_clasificado.png');
        case '9':  return this.asset('otros.png');
        case '10': return this.asset('escombros.png');
        case '11': return this.asset('servicio_aereo.png');
        case '12': return this.asset('apoyo.png');
      }
    }

    // 2) Por categoría (fallback)
    const c = (this.categoria || '').toLowerCase();

    if (c.includes('estructural') || c.includes('incendio')) return this.asset('estructural.png');
    if (c.includes('pastizal') || c.includes('forestal') || c.includes('matorral')) return this.asset('pastizal.png');
    if (c.includes('rescate vehicular')) return this.asset('rescate_vehicular.png');
    if (c.includes('vehicular') || c.includes('accidente') || c.includes('colision') || c.includes('colisión')) return this.asset('vehicular.png');
    if (c.includes('rescate')) return this.asset('rescate_personas.png');
    if (c.includes('materiales peligrosos') || c.includes('hazmat') || c.includes('químic')) return this.asset('hazmat.png');
    if (c.includes('gas')) return this.asset('gas.png');
    if (c.includes('eléctrico') || c.includes('electrico')) return this.asset('electrico.png');
    if (c.includes('escombros')) return this.asset('escombros.png');
    if (c.includes('aéreo') || c.includes('aereo')) return this.asset('servicio_aereo.png');
    if (c.includes('apoyo')) return this.asset('apoyo.png');
    if (c.includes('terror')) return this.asset('terrorismo.png');
    if (c.includes('túnel') || c.includes('tunel')) return this.asset('tunel.png');

    return this.asset('default.png');
  }
}
